import type { ReactNode } from 'react'
import AppBar from '@mui/material/AppBar'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Toolbar from '@mui/material/Toolbar'
import Typography from '@mui/material/Typography'
import LogoutIcon from '@mui/icons-material/Logout'
import SettingsIcon from '@mui/icons-material/Settings'
import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useAuth } from '@/hooks/useAuth'
import logoSvg from '@/../logo.svg'

export default function VolunteerLayout({ children }: { children: ReactNode }) {
  const { name, logout } = useAuth()
  const navigate = useNavigate()
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  const initials = (name ?? '')
    .split(' ')
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        backgroundColor: '#F8FAFC',
      }}
    >
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          backgroundColor: '#0466C8',
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between', px: { xs: 2, md: 4 } }}>
          <Box
            component="img"
            src={logoSvg}
            alt="Flock Care"
            sx={{ height: 28, filter: 'brightness(0) invert(1)' }}
          />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {name && (
              <Typography
                sx={{
                  color: '#FFFFFF',
                  fontSize: '0.875rem',
                  display: { xs: 'none', sm: 'block' },
                }}
              >
                {name}
              </Typography>
            )}
            <IconButton
              onClick={(e) => setAnchorEl(e.currentTarget)}
              size="small"
            >
              <Avatar
                sx={{
                  width: 34,
                  height: 34,
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  backgroundColor: 'rgba(255,255,255,0.18)',
                  color: '#FFFFFF',
                  border: '1.5px solid rgba(255,255,255,0.6)',
                }}
              >
                {initials}
              </Avatar>
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={() => setAnchorEl(null)}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              slotProps={{
                paper: { sx: { mt: 1, minWidth: 180, borderRadius: '12px' } },
              }}
            >
              <MenuItem
                onClick={() => {
                  setAnchorEl(null)
                  navigate({ to: '/settings' })
                }}
              >
                <ListItemIcon>
                  <SettingsIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Settings</ListItemText>
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setAnchorEl(null)
                  logout()
                }}
              >
                <ListItemIcon>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Log out</ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      <Box
        component="main"
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          maxWidth: 960,
          width: '100%',
          mx: 'auto',
          px: { xs: 2, md: 4 },
          py: { xs: 2, md: 3 },
        }}
      >
        {children}
      </Box>
    </Box>
  )
}
